import { z } from 'zod';
import { apiClient } from '@api/client';
import type { ApiResponseShape, CommonResponse } from '@/types/api';
import {
  apmcDetailSchema,
  apmcOptionSchema,
  apmcPickerOptionSchema,
  apmcSchema,
  type Apmc,
  type ApmcDetail,
  type ApmcFormValues,
  type ApmcOption,
  type ApmcPickerOption,
  type ListApmcsParams,
} from './schema';

const BASE_PATH = '/apmcs';

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(apmcSchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number(),
      isLastPage: z.boolean(),
    })
    .optional(),
});

const detailResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: apmcDetailSchema.nullable(),
  error: z.string().nullable().optional(),
});

const recordResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: apmcSchema.nullable(),
  error: z.string().nullable().optional(),
});

const optionsResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: z.array(apmcOptionSchema).nullable(),
  error: z.string().nullable().optional(),
});

const pickerOptionsResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: z.array(apmcPickerOptionSchema).nullable(),
  error: z.string().nullable().optional(),
});

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

function toWireTranslations(values: ApmcFormValues) {
  return values.translations
    .filter((t) => t.name.trim().length > 0)
    .map((t) => ({ localeCode: t.localeCode, name: t.name.trim() }));
}

function toCreatePayload(values: ApmcFormValues) {
  return {
    districtId: Number(values.districtId),
    code: values.code,
    name: values.name,
    address: values.address ?? undefined,
    latitude: values.latitude ?? undefined,
    longitude: values.longitude ?? undefined,
    isActive: values.isActive,
    translations: toWireTranslations(values),
  };
}

// `districtId` and `code` intentionally absent — both immutable post-create.
function toUpdatePayload(values: ApmcFormValues) {
  return {
    name: values.name,
    address: values.address,
    latitude: values.latitude,
    longitude: values.longitude,
    isActive: values.isActive,
    translations: toWireTranslations(values),
  };
}

export async function listApmcs(
  params: ListApmcsParams,
): Promise<CommonResponse<Apmc>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

export async function getApmc(id: string): Promise<ApmcDetail> {
  const response = await apiClient.get(`${BASE_PATH}/${id}`);
  const parsed = detailResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function getApmcOptions(): Promise<ApmcOption[]> {
  const response = await apiClient.get(`${BASE_PATH}/options`);
  const parsed = optionsResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

/** Rich active-list for M:N pickers (closure form etc.) — grouped by state on the client. */
export async function getApmcPickerOptions(): Promise<ApmcPickerOption[]> {
  const response = await apiClient.get(`${BASE_PATH}/picker-options`);
  const parsed = pickerOptionsResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function createApmc(values: ApmcFormValues): Promise<Apmc> {
  const response = await apiClient.post<ApiResponseShape<Apmc>>(
    BASE_PATH,
    toCreatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function updateApmc(
  id: string,
  values: ApmcFormValues,
): Promise<Apmc> {
  const response = await apiClient.put<ApiResponseShape<Apmc>>(
    `${BASE_PATH}/${id}`,
    toUpdatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}
